import { computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { ALLACCESS, ESSENTIALS, FREE } from "@/consts/bundles";
import newCard from "@/utils/newCard";

export interface CardResult {
  type?: string;
  author?: string;
  name?: string;
  title?: string;
  summary?: string;
  acronym?: string;
  content_bundle?: string[];
  global_products?: any[];
  lab_type?: string;
  bundle_data?: {
    name?: string;
    title?: string;
  };
  catalog_data?: {
    type?: string;
    author?: string;
    includes?: any[];
    certification_data?: {
      certification_name?: string;
      certification_level?: string;
    }[];
  };
  lock_content?: boolean;
  content_retired?: string;
  certifications?: string[];
  certifications_data?: {
    certification_guid?: string;
    certification_name?: string;
    certification_level?: string;
  }[];
  guid?: string;
  is_partial?: boolean;
  includes?: any[];
  date?: string;
  offer_bundle_details?: {
    certification_level?: string;
  };
}

export interface CardProps {
  result: CardResult;
  status?: any;
  forCarousel?: boolean;
  isUnauth?: boolean;
  isAdminContent?: boolean;
  isRemovable?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
}

export function useCardData(props: CardProps) {
  const store = useStore();
  const route = useRoute();

  // Store getters
  const inMyList = computed(() => store.getters["user/inMyList"]);
  const userEntitlements = computed(() => store.getters["user/entitlements"]);
  const prices = computed(() => store.getters["pricing/prices"]);
  const freeId = computed(() => store.getters["content/freeId"]);
  const essentialsId = computed(() => store.getters["content/essentialsId"]);
  const allAccessId = computed(() => store.getters["content/allAccessId"]);

  // Basic computed properties
  const displayName = computed(() => {
    if (props.result?.name) return props.result.name;
    if (props.result?.title) return props.result.title;
    if (props.result?.bundle_data?.name) return props.result.bundle_data.name;
    if (props.result?.bundle_data?.title) return props.result.bundle_data.title;
    return "";
  });

  const authorName = computed((): string => {
    // For bundles, don't show author in header
    if (isBundle.value) return "";
    if (props.result?.author) return props.result.author;
    if (props.result?.catalog_data?.author)
      return props.result.catalog_data.author;
    return "";
  });

  const isBundle = computed((): boolean => {
    return (
      props.result?.type === "bundle" ||
      props.result?.catalog_data?.type === "bundle"
    );
  });

  const retiredContent = computed(() => {
    return props.result?.content_retired === "RETIRED";
  });

  const resultCertifications = computed((): string => {
    // Only graded-labs show certification name in related-certs section
    // standalone-labs don't show certification name
    if (
      props.result?.type === "lab" &&
      props.result?.lab_type === "graded-labs" &&
      props.result?.certifications_data?.length
    ) {
      return (
        props.result.certifications_data
          ?.map((cert) => cert.certification_name)
          .join(", ") ?? ""
      );
    }
    if (props.result?.certifications) {
      return props.result.certifications.join(", ");
    }
    if (props.result?.type !== "bundle") {
      return "";
    }
    const certificationLevel =
      props.result?.offer_bundle_details?.certification_level;
    if (!certificationLevel) {
      return "";
    }
    const priorityTypes = ["path", "practice-exam"];
    for (const type of priorityTypes) {
      const certNames = getCertificationsByType(type, certificationLevel);
      if (certNames.length > 0) {
        return certNames.join(", ");
      }
    }
    return "";
  });

  const getCertificationsByType = (
    type: string,
    certificationLevel: string
  ): string[] => {
    const catalogData = props.result?.catalog_data;
    if (!catalogData || !Array.isArray(catalogData)) {
      return [];
    }
    const item = catalogData.find((item: any) => item.type === type);
    if (!item?.certification_data) {
      return [];
    }
    let certNames = item.certification_data
      .filter((cert: any) => cert.certification_level === certificationLevel)
      .map((cert: any) => cert.certification_name)
      .filter((name: any) => name && name.trim() !== "");
    return Array.from(new Set(certNames));
  };
  const enableCertifications = computed(() => {
    return globalThis.env.ENABLE_CERT_FILTER_LIST;
  });

  // Content bundle checks
  const hasContentInGlobalProducts = (contentId: string): boolean => {
    return (
      props.result?.global_products?.some(
        (product: any) => product?.content_bundle_id === contentId
      ) || false
    );
  };

  const freeContent = computed((): boolean => {
    const contentBundle = props.result?.content_bundle;
    if (contentBundle?.includes(freeId.value)) return true;
    return hasContentInGlobalProducts(freeId.value);
  });

  const essentialsContent = computed((): boolean => {
    const contentBundle = props.result?.content_bundle;
    if (contentBundle?.includes(essentialsId.value)) return true;
    return hasContentInGlobalProducts(essentialsId.value);
  });

  const allAccessContent = computed((): boolean => {
    const contentBundle = props.result?.content_bundle;
    if (contentBundle?.includes(allAccessId.value)) return true;
    return hasContentInGlobalProducts(allAccessId.value);
  });

  // User entitlement checks
  const hasAllAccess = computed((): boolean => {
    return userEntitlements.value?.some(
      (entitlement: any) => entitlement?.content_bundle_name === ALLACCESS
    );
  });

  const hasEssentials = computed((): boolean => {
    return userEntitlements.value?.some(
      (entitlement: any) => entitlement?.content_bundle_name === ESSENTIALS
    );
  });

  const hasEssentialsWithNoAllAccess = computed((): boolean => {
    return hasEssentials.value && !hasAllAccess.value;
  });

  const hasFree = computed((): boolean => {
    return userEntitlements.value?.some(
      (entitlement: any) =>
        entitlement?.content_bundle_name === FREE &&
        !hasEssentialsWithNoAllAccess.value &&
        !hasAllAccess.value
    );
  });

  // Route helpers
  const routePath = computed(() => route?.path ?? "");
  const routerName = computed((): string => {
    const name = route?.name;
    return typeof name === "string" ? name : "";
  });

  // Page checks
  const isStoreAllProductsPage = computed((): boolean => {
    return ["all-products", "type-products"].includes(routerName.value);
  });

  const storePlansPage = computed((): boolean => {
    return ["plans"].includes(routerName.value);
  });

  const contentBundlePage = computed((): boolean => {
    return routePath.value.includes("/content-bundle");
  });

  const isStorePage = computed(() => {
    return route.meta.storePage === true;
  });

  // Card data
  const cardData = computed(() => {
    return newCard.dataObject(
      props.result,
      props.status,
      isStoreAllProductsPage.value
    );
  });

  // Locking logic
  const isLocked = computed((): boolean => {
    if (freeContent.value) {
      return !freeContent.value;
    }
    if (globalThis.env.ENABLE_LOCK_CONTENT) {
      return Boolean(props.result?.lock_content);
    }
    return cardData.value.premium;
  });

  const canItBeRemoved = computed(() => {
    const isPremium = freeContent.value
      ? !freeContent.value
      : cardData.value.premium;
    return (props.isRemovable || props.isAdminContent) && !isPremium;
  });

  const customerAdminLink = computed(() => {
    return globalThis.env.CUSTOMER_ADMIN_URL + "/learners/profile";
  });

  return {
    // Store getters
    inMyList,
    userEntitlements,
    prices,
    freeId,
    essentialsId,
    allAccessId,

    // Basic properties
    displayName,
    authorName,
    isBundle,
    retiredContent,
    resultCertifications,
    enableCertifications,

    // Content checks
    freeContent,
    essentialsContent,
    allAccessContent,
    hasContentInGlobalProducts,

    // User entitlements
    hasAllAccess,
    hasEssentials,
    hasEssentialsWithNoAllAccess,
    hasFree,

    // Route helpers
    routePath,
    routerName,
    isStoreAllProductsPage,
    storePlansPage,
    contentBundlePage,
    isStorePage,

    // Card data
    cardData,
    isLocked,
    canItBeRemoved,
    customerAdminLink,

    // Methods
    getCertificationsByType,
  };
}
